import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    FormGroup,
    Label,
    Spinner,
} from 'reactstrap';
import { getData, postData } from '../../../CommonElements/Functions/PostGetFun';
import { API } from '../../../api/API';
import { toast } from 'react-toastify';
import moment from 'moment';
import FeatherIconCom from '../../../Utils/CommonComponents/CommonIcons/FeatherIconCom';
import Loader from '../../../Layout/Loader';
import { isOwnRequest } from '../../../Utils/authUtils';

export default function GeoFencingApprovals() {
    const serverUrl = localStorage.getItem("serverUrl");
    const [loading, setLoading] = useState<boolean>(true);
    const [requests, setRequests] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('all');

    // Modal state for Approve / Reject
    const [actionModalType, setActionModalType] = useState<'approve' | 'reject' | null>(null);
    const [targetReqItem, setTargetReqItem] = useState<any>(null);
    const [rejectRemark, setRejectRemark] = useState<string>('');
    const [submittingAction, setSubmittingAction] = useState<boolean>(false);

    // Map Modal
    const [mapModalOpen, setMapModalOpen] = useState<boolean>(false);
    const [selectedGpsCoords, setSelectedGpsCoords] = useState<string | null>(null);

    const fetchRequests = async () => {
        if (!serverUrl) return;
        setLoading(true);
        try {
            const url = `${serverUrl}${API.getHolidayRequests}`;
            const response = await getData(url, true, { module_name: 'geo_fencing' });

            if (response.status === 200 || response.data?.statusCode === 200) {
                let list: any[] = [];
                const d = response.data;
                if (Array.isArray(d)) list = d;
                else if (Array.isArray(d?.data)) list = d.data;
                else if (Array.isArray(d?.items)) list = d.items;
                else if (d && typeof d === 'object' && !Array.isArray(d)) list = [d];

                setRequests(list);
            } else {
                setRequests([]);
            }
        } catch (error) {
            console.error("Error fetching geofencing requests:", error);
            toast.error("Failed to fetch geofencing requests");
            setRequests([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, [serverUrl]);

    const handleOpenActionModal = (reqItem: any, type: 'approve' | 'reject') => {
        setTargetReqItem(reqItem);
        setActionModalType(type);
        setRejectRemark('');
    };

    const handleConfirmAction = async () => {
        if (!targetReqItem || !actionModalType || !serverUrl) return;

        if (actionModalType === 'reject' && !rejectRemark.trim()) {
            toast.error("Please enter a rejection remark");
            return;
        }

        const reqId = targetReqItem.holiday_req_id || targetReqItem.id;

        setSubmittingAction(true);
        try {
            const url = `${serverUrl}${API.actionHolidayRequest}`;
            const payload: any = {
                module_name: 'geo_fencing',
                holiday_req_id: reqId,
                status: actionModalType === 'approve' ? 'approved' : 'rejected',
            };
            if (actionModalType === 'reject') {
                payload.reject_remark = rejectRemark.trim();
            }

            const response = await postData(url, true, payload);
            const ok = response.status === 200 || response.data?.statusCode === 200;

            if (ok) {
                toast.success(response.data?.message || `Geofencing request ${actionModalType === 'approve' ? 'approved' : 'rejected'} successfully!`);
                setActionModalType(null);
                setTargetReqItem(null);
                fetchRequests();
            } else {
                toast.error(response.data?.message || response.data?.error || `Failed to ${actionModalType} request`);
            }
        } catch (error: any) {
            console.error("Geofencing action error:", error);
            toast.error(error?.message || "An error occurred while performing action");
        } finally {
            setSubmittingAction(false);
        }
    };

    // Summary counts
    const stats = useMemo(() => {
        const total = requests.length;
        const pending = requests.filter(r => (r.status || r.Status || 'pending').toLowerCase() === 'pending').length;
        const approved = requests.filter(r => (r.status || r.Status || '').toLowerCase() === 'approved').length;
        const rejected = requests.filter(r => (r.status || r.Status || '').toLowerCase() === 'rejected').length;
        return { total, pending, approved, rejected };
    }, [requests]);

    const getStatusBadgeColor = (status: string): string => {
        switch ((status || '').toLowerCase()) {
            case 'approved': return 'success';
            case 'rejected': return 'danger';
            case 'pending': return 'warning';
            default: return 'secondary';
        }
    };

    const filteredRequests = useMemo(() => {
        return requests.filter((item) => {
            const status = (item.status || item.Status || 'pending').toLowerCase();

            if (selectedStatusFilter !== 'all' && status !== selectedStatusFilter) {
                return false;
            }

            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase();
                const empName = (item.employee_name || item.requested_by_name || item.name || '').toLowerCase();
                const outletName = (item.outlet_name || '').toLowerCase();
                const outletCode = (item.outlet_code || '').toLowerCase();
                const townName = (item.town_name || '').toLowerCase();
                const beatName = (item.beat_name || '').toLowerCase();

                return (
                    empName.includes(term) ||
                    outletName.includes(term) ||
                    outletCode.includes(term) ||
                    townName.includes(term) ||
                    beatName.includes(term)
                );
            }

            return true;
        });
    }, [requests, selectedStatusFilter, searchTerm]);

    return (
        <Container fluid className="p-0">
            {/* Header & Controls Bar */}
            <Card className="mb-3 shadow-sm border-0" style={{ borderRadius: '10px' }}>
                <CardBody className="p-3">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <div className="d-flex align-items-center gap-2">
                            <h4 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2" style={{ color: '#000000' }}>
                                <FeatherIconCom iconName="MapPin" size={20} className="text-primary" />
                                GeoFencing Approvals
                            </h4>
                            <Badge color="primary" pill className="px-2.5 py-1 fs-6">
                                {filteredRequests.length}
                            </Badge>
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="position-relative" style={{ minWidth: '220px' }}>
                                <Input
                                    type="text"
                                    placeholder="Search employee, outlet..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="ps-5"
                                    style={{ borderRadius: '6px', fontSize: '0.88rem' }}
                                />
                                <FeatherIconCom
                                    iconName="Search"
                                    size={15}
                                    className="position-absolute text-muted"
                                    style={{ left: '12px', top: '50%', transform: 'translateY(-50%)' }}
                                />
                            </div>

                            <Input
                                type="select"
                                value={selectedStatusFilter}
                                onChange={(e) => setSelectedStatusFilter(e.target.value)}
                                style={{ borderRadius: '6px', fontSize: '0.88rem', minWidth: '150px' }}
                            >
                                <option value="all">All Status ({stats.total})</option>
                                <option value="pending">Pending ({stats.pending})</option>
                                <option value="approved">Approved ({stats.approved})</option>
                                <option value="rejected">Rejected ({stats.rejected})</option>
                            </Input>

                            <Button
                                color="dark"
                                outline
                                size="sm"
                                className="d-flex align-items-center gap-1"
                                onClick={fetchRequests}
                                style={{ borderRadius: '6px' }}
                            >
                                <FeatherIconCom iconName="RefreshCw" size={14} />
                                <span>Refresh</span>
                            </Button>
                        </div>
                    </div>

                    {/* Filter Pills Bar */}
                    <div className="d-flex gap-2 flex-wrap pt-2 border-top">
                        <Badge
                            color={selectedStatusFilter === 'all' ? 'primary' : 'light'}
                            className={`px-3 py-2 cursor-pointer fw-semibold text-capitalize ${selectedStatusFilter !== 'all' ? 'text-dark border' : ''}`}
                            style={{ cursor: 'pointer', fontSize: '0.82rem', borderRadius: '6px' }}
                            onClick={() => setSelectedStatusFilter('all')}
                        >
                            Total Requests ({stats.total})
                        </Badge>
                        <Badge
                            color={selectedStatusFilter === 'pending' ? 'warning' : 'light'}
                            className={`px-3 py-2 cursor-pointer fw-semibold text-capitalize ${selectedStatusFilter !== 'pending' ? 'text-dark border' : ''}`}
                            style={{ cursor: 'pointer', fontSize: '0.82rem', borderRadius: '6px' }}
                            onClick={() => setSelectedStatusFilter('pending')}
                        >
                            Pending ({stats.pending})
                        </Badge>
                        <Badge
                            color={selectedStatusFilter === 'approved' ? 'success' : 'light'}
                            className={`px-3 py-2 cursor-pointer fw-semibold text-capitalize ${selectedStatusFilter !== 'approved' ? 'text-dark border' : ''}`}
                            style={{ cursor: 'pointer', fontSize: '0.82rem', borderRadius: '6px' }}
                            onClick={() => setSelectedStatusFilter('approved')}
                        >
                            Approved ({stats.approved})
                        </Badge>
                        <Badge
                            color={selectedStatusFilter === 'rejected' ? 'danger' : 'light'}
                            className={`px-3 py-2 cursor-pointer fw-semibold text-capitalize ${selectedStatusFilter !== 'rejected' ? 'text-dark border' : ''}`}
                            style={{ cursor: 'pointer', fontSize: '0.82rem', borderRadius: '6px' }}
                            onClick={() => setSelectedStatusFilter('rejected')}
                        >
                            Rejected ({stats.rejected})
                        </Badge>
                    </div>
                </CardBody>
            </Card>

            {/* Content Cards Grid */}
            {loading ? (
                <Loader height="250px" />
            ) : filteredRequests.length === 0 ? (
                <Card className="shadow-sm border-0 text-center py-5" style={{ borderRadius: '10px' }}>
                    <CardBody>
                        <FeatherIconCom iconName="MapPin" size={44} className="text-muted mb-3" />
                        <h5 className="fw-bold text-dark mb-1">No GeoFencing Requests Found</h5>
                        <p className="text-muted mb-0 small">There are no location override or geofencing requests matching your filter.</p>
                    </CardBody>
                </Card>
            ) : (
                <Row className="g-3">
                    {filteredRequests.map((item, idx) => {
                        const status = (item.status || item.Status || 'pending').toLowerCase();
                        const isOwn = isOwnRequest(item);
                        const empName = item.employee_name || item.requested_by_name || item.name || 'Unknown Employee';
                        const reqDate = item.requested_date || item.created_at ? moment(item.requested_date || item.created_at).format('DD MMM YYYY, hh:mm A') : 'N/A';
                        const gps = item.outlet_gps || item.latlng;
                        const isPending = status === 'pending';

                        return (
                            <Col key={item.id || item.holiday_req_id || idx} lg={6} md={12}>
                                <Card className="border shadow-sm bg-white h-100" style={{ borderRadius: '10px', overflow: 'hidden', borderColor: '#cccccc' }}>
                                    {/* Card Header Info */}
                                    <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
                                        <div>
                                            <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2" style={{ color: '#000000', fontSize: '1rem' }}>
                                                {empName}
                                                {isOwn && (
                                                    <Badge color="secondary" className="px-2 py-0.5 text-white" style={{ fontSize: '0.65rem' }}>
                                                        Own Request
                                                    </Badge>
                                                )}
                                            </h6>
                                            <div className="text-muted small mt-0.5" style={{ fontSize: '0.78rem' }}>
                                                <FeatherIconCom iconName="Calendar" size={12} className="me-1" />
                                                <span>Requested: </span>
                                                <span className="fw-semibold text-dark">{reqDate}</span>
                                            </div>
                                        </div>

                                        <Badge color={getStatusBadgeColor(status)} className="px-3 py-1.5 fw-bold text-uppercase" style={{ fontSize: '0.78rem' }}>
                                            {status}
                                        </Badge>
                                    </div>

                                    {/* Card Body Info */}
                                    <CardBody className="p-3 d-flex flex-column justify-content-between">
                                        <div>
                                            <div className="bg-light p-2.5 rounded mb-2" style={{ fontSize: '0.88rem' }}>
                                                {/* Outlet info */}
                                                {(item.outlet_name || item.outlet_code) && (
                                                    <div className="d-flex align-items-center gap-2 mb-1.5">
                                                        <FeatherIconCom iconName="Home" size={15} className="text-primary flex-shrink-0" />
                                                        <span className="fw-bold text-dark">{item.outlet_name || 'Outlet'}</span>
                                                        {item.outlet_code && (
                                                            <Badge color="info" className="px-2 py-0.5 fw-bold text-white" style={{ fontSize: '0.72rem' }}>
                                                                {item.outlet_code}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Town / Beat */}
                                                {(item.town_name || item.beat_name) && (
                                                    <div className="d-flex align-items-center gap-2 mb-1.5 text-muted small">
                                                        <FeatherIconCom iconName="Navigation" size={14} className="text-secondary flex-shrink-0" />
                                                        <span>{[item.town_name, item.beat_name].filter(Boolean).join(' • ')}</span>
                                                    </div>
                                                )}

                                                {/* GPS Coordinates Button */}
                                                {gps && (
                                                    <div className="d-flex align-items-center gap-2 mb-1.5">
                                                        <FeatherIconCom iconName="MapPin" size={14} className="text-danger flex-shrink-0" />
                                                        <Button
                                                            color="link"
                                                            size="sm"
                                                            className="p-0 text-primary fw-semibold d-inline-flex align-items-center gap-1 text-decoration-none"
                                                            style={{ fontSize: '0.84rem' }}
                                                            onClick={() => {
                                                                setSelectedGpsCoords(gps);
                                                                setMapModalOpen(true);
                                                            }}
                                                        >
                                                            <FeatherIconCom iconName="Map" size={13} />
                                                            <span>GPS: {gps} (View Map)</span>
                                                        </Button>
                                                    </div>
                                                )}

                                                {/* Reason */}
                                                {item.reason && (
                                                    <div className="text-muted small mt-1">
                                                        <strong className="text-dark">Reason:</strong> {item.reason}
                                                    </div>
                                                )}

                                                {/* Rejection Remarks */}
                                                {status === 'rejected' && item.reject_remark && (
                                                    <div className="text-danger small mt-1">
                                                        <strong>Rejection Remark:</strong> {item.reject_remark}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Action Buttons Footer */}
                                        {isPending && !isOwn && (
                                            <div className="d-flex align-items-center justify-content-end gap-2 pt-2 border-top">
                                                <Button
                                                    color="danger"
                                                    size="sm"
                                                    className="px-3 py-1 fw-bold text-white me-1"
                                                    style={{ backgroundColor: '#EF5350', borderColor: '#EF5350', borderRadius: '4px' }}
                                                    onClick={() => handleOpenActionModal(item, 'reject')}
                                                >
                                                    <FeatherIconCom iconName="X" size={14} className="me-1" />
                                                    Reject
                                                </Button>
                                                <Button
                                                    color="success"
                                                    size="sm"
                                                    className="px-3 py-1 fw-bold text-white me-1"
                                                    style={{ backgroundColor: '#6cbb33', borderColor: '#6cbb33', borderRadius: '4px' }}
                                                    onClick={() => handleOpenActionModal(item, 'approve')}
                                                >
                                                    <FeatherIconCom iconName="Check" size={14} className="me-1" />
                                                    Approve
                                                </Button>
                                            </div>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}

            {/* Action Modal (Approve / Reject) */}
            <Modal isOpen={actionModalType !== null} toggle={() => setActionModalType(null)} centered size="md">
                <ModalHeader toggle={() => setActionModalType(null)} className={actionModalType === 'approve' ? 'bg-success text-white' : 'bg-danger text-white'}>
                    <FeatherIconCom iconName={actionModalType === 'approve' ? 'CheckCircle' : 'AlertTriangle'} size={18} className="me-2" />
                    {actionModalType === 'approve' ? 'Approve GeoFencing Request' : 'Reject GeoFencing Request'}
                </ModalHeader>
                <ModalBody className="p-4" style={{ color: '#000000' }}>
                    <p className="text-dark mb-3" style={{ fontSize: '0.95rem' }}>
                        Are you sure you want to <strong>{actionModalType}</strong> the location geofencing request for{' '}
                        <strong className="text-primary">{targetReqItem?.employee_name || targetReqItem?.requested_by_name || targetReqItem?.name || 'this employee'}</strong>?
                    </p>

                    {actionModalType === 'reject' && (
                        <FormGroup className="mb-0">
                            <Label className="form-label fw-bold text-dark small">Rejection Remark <span className="text-danger">*</span></Label>
                            <Input
                                type="textarea"
                                rows={3}
                                placeholder="Enter reason for rejection..."
                                value={rejectRemark}
                                onChange={(e) => setRejectRemark(e.target.value)}
                                style={{ borderRadius: '6px' }}
                            />
                        </FormGroup>
                    )}
                </ModalBody>
                <ModalFooter className="bg-light">
                    <Button color="secondary" outline size="sm" onClick={() => setActionModalType(null)} disabled={submittingAction}>
                        Cancel
                    </Button>
                    <Button
                        color={actionModalType === 'approve' ? 'success' : 'danger'}
                        size="sm"
                        className="text-white px-4 fw-semibold"
                        onClick={handleConfirmAction}
                        disabled={submittingAction}
                    >
                        {submittingAction ? <Spinner size="sm" color="light" /> : `Confirm ${actionModalType === 'approve' ? 'Approve' : 'Reject'}`}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* GPS Map Modal */}
            <Modal isOpen={mapModalOpen} toggle={() => setMapModalOpen(false)} centered size="lg">
                <ModalHeader toggle={() => setMapModalOpen(false)} className="bg-light border-bottom">
                    <FeatherIconCom iconName="MapPin" size={18} className="me-2 text-primary" />
                    GPS Location Coordinates: {selectedGpsCoords}
                </ModalHeader>
                <ModalBody className="p-0" style={{ height: '420px' }}>
                    {selectedGpsCoords ? (
                        <iframe
                            title="Location Map"
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            src={`https://maps.google.com/maps?q=${selectedGpsCoords}&z=16&output=embed`}
                        />
                    ) : (
                        <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                            No coordinates available
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="bg-light p-2">
                    <Button color="secondary" size="sm" onClick={() => setMapModalOpen(false)}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
}
